import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, FileText, FolderOpen, Loader2, MoreHorizontal, Package, PhoneCall, ScanSearch, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { cn, firstName } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useMediaQuery } from "@/hooks/use-media-query";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DocViewer } from "@/components/canvas/DocViewer";
import { LosMark } from "@/components/Logo";
import { IntakeComposer } from "@/components/intake/IntakeComposer";
import { IntakeChat } from "@/components/intake/IntakeChat";
import { useIntake } from "@/hooks/useIntake";
import { filesFromDrop } from "@/lib/dropFiles";
import { intakeProgress } from "@/lib/intake";
import { takeIntake } from "@/lib/intakeHandoff";

const EASE = [0.22, 1, 0.36, 1];
const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

/** Three quiet ways in – they seed the field or open the picker; nothing is created by clicking them. */
const STARTERS = [
  { key: "veiviser", label: "Nytt tilbud med veiviser", icon: Wand2, to: "/ny/veiviser", primary: true },
  { key: "mulighet", label: "Registrer mulighet", icon: PhoneCall, to: "/ny/mulighet", title: "Etter en telefon eller et møte – før forespørselen er kommet" },
  { key: "analyser", label: "Last opp kundens dokumenter", icon: ScanSearch, pick: true },
  { key: "tilbud", label: "Beskriv oppdraget", icon: FileText, seed: "Lag et tilbud basert på denne forespørselen: " },
  { key: "produkter", label: "Finn produkter og priser", icon: Package, to: "/products" },
  { key: "eksempel", label: "Prøv med eksempelmappe", icon: FolderOpen, example: true, title: "En oppdiktet kundemappe med 36 filer (jobbliste i Excel, arbeidsordrer, skannede PDF-er og bilder) – hele løpet fra mappe til tilbud" },
];

/** The mark and the one question – gone softly the moment the conversation starts. */
function Hero({ name }) {
  return (
    <motion.div key="hero" className="flex flex-col items-center" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14, transition: { duration: 0.22, ease: EASE } }} transition={{ duration: 0.45, ease: EASE }} data-testid="intake-hero">
      <LosMark tone="bare" className="h-9 w-9" testId="home-mark" />
      <h1 className="mt-6 text-center font-sans text-[30px] font-medium leading-[1.15] tracking-[-0.022em] text-foreground sm:text-[32px]" data-testid="home-title">{name ? `Hva vil du jobbe med, ${name}?` : "Hva vil du jobbe med?"}</h1>
    </motion.div>
  );
}

function Starters({ onPick, loading }) {
  return (
    <motion.div key="starters" className="mt-5 flex flex-wrap items-center justify-center gap-2" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.12, duration: 0.4, ease: EASE } }} exit={{ opacity: 0, transition: { duration: 0.15 } }} data-testid="home-starters">
      {STARTERS.map((s) => {
        const Icon = s.icon;
        return (
          <button key={s.key} type="button" onClick={() => onPick(s)} disabled={Boolean(loading)} title={s.title} className={cn("disabled:opacity-60","inline-flex h-9 items-center gap-2 rounded-full border px-3.5 text-[13.5px] font-medium transition-[border-color,color,background-color,transform] duration-150 active:scale-[0.98]", s.primary ? "border-[hsl(var(--navy))] bg-[hsl(var(--navy))] text-white shadow-[0_10px_22px_-16px_hsl(var(--navy)/0.9)] hover:bg-[hsl(var(--ink-2))]" : "border-black/[0.08] bg-white text-foreground/70 hover:border-black/[0.16] hover:bg-black/[0.02] hover:text-foreground")} data-testid={`home-starter-${s.key}`}>
            {loading === s.key ? <Loader2 className="h-4 w-4 animate-spin text-foreground/50" /> : <Icon className={cn("h-4 w-4", s.primary ? "text-white/85" : "text-foreground/50")} strokeWidth={1.8} />}{loading === s.key ? "Henter eksempelmappen …" : s.label}
          </button>
        );
      })}
    </motion.div>
  );
}

/**
 * /ny – one surface, two moments. Before: the mark, one question and the composer, centred; files you drop anywhere on
 * the page sit in the composer while they upload quietly. On send the hero fades, the composer settles at the bottom
 * and the conversation starts in the same frame – the assistant reads, sums up and offers the next step. Unfinished
 * intakes live in the left panel, like earlier chats.
 */
export default function IntakePage() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sm = useMediaQuery("(min-width: 640px)");
  const createdHere = useRef(null);
  const onSession = useCallback((id) => { createdHere.current = id; navigate(`/ny/${id}`, { replace: true }); }, [navigate]);
  const intake = useIntake({ sid: sid || null, onSession });
  const [staged, setStaged] = useState(!sid);
  const [viewer, setViewer] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dropping, setDropping] = useState(false);
  const dropDepth = useRef(0);
  const composer = useRef(null);
  const { session, documents, rows, busy } = intake;
  const prevSid = useRef(sid);

  useEffect(() => {
    const p = takeIntake();
    if (p?.files?.length) intake.addFiles(p.files);
    if (p?.text) intake.send(p.text);
    if (p?.files?.length || p?.text) setStaged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // «Nytt arbeid» while a conversation is open: back to the empty composer, nothing deleted. Opening a saved intake from
  // the panel (sid appears without us creating it) shows the conversation.
  useEffect(() => {
    if (prevSid.current && !sid) { intake.reset(); createdHere.current = null; setStaged(true); }
    else if (!prevSid.current && sid && createdHere.current !== sid) setStaged(false);
    prevSid.current = sid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  // Documents dropped on the hero are read unasked: once the findings are in, the conversation opens to show them.
  useEffect(() => {
    if (staged && session?.proposal && rows.some((r) => !["feil", "duplikat"].includes(r.status)) && !busy) setStaged(false);
  }, [staged, session?.proposal, rows, busy]);

  const docs = useMemo(() => (documents || []).filter((d) => d.status !== "duplikat"), [documents]);
  const viewerDoc = viewer ? docs.find((d) => d.id === viewer.docId) : null;
  const created = session?.status === "created";
  const create = async (target) => {
    const into = typeof target === "string" ? target : null;
    setCreating(true);
    const r = await intake.createWork(into);
    if (!r) { setCreating(false); return; }
    toast.success(into ? `Forespørselen er lagt i muligheten ${r.project.number}` : `${r.project.name} er opprettet som sak ${r.project.number}`);
    navigate(`/p/${r.project.id}`);
  };
  const discard = async () => { await intake.discard(); setStaged(true); navigate("/ny", { replace: true }); };
  const send = (text) => { if (staged) setStaged(false); if (text) intake.send(text); };
  const [loadingStarter, setLoadingStarter] = useState(null);
  const tryExample = async () => {
    setLoadingStarter("eksempel");
    try { const r = await api.post("/inntak/eksempel"); navigate(`/ny/${r.data.id}`); } catch (e) { toast.error(errMsg(e)); } finally { setLoadingStarter(null); }
  };
  const pick = (s) => { if (s.example) tryExample(); else if (s.to) navigate(s.to); else if (s.pick) composer.current?.pickFiles(); else composer.current?.seed(s.seed); };

  // A file can land anywhere on the page – the whole surface answers, the composer receives.
  const onDragEnter = (e) => { if (!isFileDrag(e) || created) return; e.preventDefault(); dropDepth.current += 1; setDropping(true); };
  const onDragOver = (e) => { if (!isFileDrag(e) || created) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
  const onDragLeave = (e) => { if (!isFileDrag(e)) return; dropDepth.current = Math.max(0, dropDepth.current - 1); if (!dropDepth.current) setDropping(false); };
  const onDrop = (e) => { if (!isFileDrag(e)) return; e.preventDefault(); dropDepth.current = 0; setDropping(false); if (created) return; filesFromDrop(e.dataTransfer).then((files) => { if (files.length) intake.addFiles(files); }); };

  const title = session?.findings?.title?.value || session?.source_request?.subject || "Nytt arbeid";

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white" data-testid="intake-page" data-state={staged ? "empty" : session?.status || "open"} data-dropping={dropping ? "1" : "0"} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <AnimatePresence>
        {dropping ? (
          <motion.div key="veil" className="pointer-events-none absolute inset-3 z-30 flex items-center justify-center rounded-[24px] border border-dashed border-[hsl(var(--navy)/0.35)] bg-white/70 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: 0.16 } }} exit={{ opacity: 0, transition: { duration: 0.12 } }} data-testid="intake-drop-veil">
            <div className="text-center"><div className="text-[16px] font-medium text-foreground">Slipp dokumentene her</div><p className="mt-1 text-[13.5px] text-muted-foreground">De leses først – ingenting opprettes før du sier fra.</p></div>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {!staged ? (
          <motion.header key="head" className="flex h-14 shrink-0 items-center justify-between gap-3 px-5 sm:px-8" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.25, duration: 0.3 } }} exit={{ opacity: 0, transition: { duration: 0.15 } }} data-testid="intake-header">
            <div className="flex min-w-0 items-baseline gap-2.5">
              <h1 className="truncate text-[15px] font-semibold tracking-[-0.01em]" data-testid="intake-title">{title}</h1>
              <span className="hidden text-[12.5px] text-muted-foreground sm:inline" data-testid="intake-saved">{created ? "Arbeidet er opprettet" : session ? (session.source_request ? "Fra innboksen · lagres automatisk" : "Lagres automatisk") : ""}</span>
            </div>
            <div className="flex items-center gap-1">
              {created && session.case_id ? <Link to={`/p/${session.case_id}`} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-4 text-[13.5px] font-semibold text-white hover:bg-[hsl(var(--ink-2))]" data-testid="intake-open-case">Åpne arbeidet <ArrowRight className="h-4 w-4" /></Link> : null}
              {!created ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button type="button" className="flex h-8 w-8 items-center justify-center rounded-[8px] text-muted-foreground outline-none transition-colors hover:bg-black/[0.05] hover:text-foreground data-[state=open]:bg-black/[0.06]" aria-label="Flere valg" data-testid="intake-menu"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={6} className="w-56 rounded-xl border-black/[0.06] p-1.5 shadow-float">
                    <DropdownMenuItem onSelect={discard} className="h-9 cursor-pointer gap-2.5 rounded-lg px-2 text-[13px] [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-muted-foreground" data-testid="intake-discard"><Trash2 /> Forkast inntaket</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </motion.header>
        ) : null}
      </AnimatePresence>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className={cn("flex min-h-0 flex-1 flex-col", staged && "items-center justify-end px-4 sm:px-8")}>
          <AnimatePresence initial={false}>{staged ? <Hero name={firstName(user?.name)} /> : null}</AnimatePresence>
          {!staged ? (
            intake.loadError
              ? <div className="mx-auto mt-10 max-w-[560px] rounded-xl bg-[hsl(0_50%_46%/0.06)] px-4 py-3 text-[14px] text-[hsl(var(--v-blocked))]" data-testid="intake-load-error">{intake.loadError}</div>
              : <motion.div key="chat" className="flex min-h-0 flex-1 flex-col" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2, duration: 0.35 } }} data-testid="intake-chat"><IntakeChat intake={intake} onOpenDoc={(docId, where) => setViewer({ docId, where: where || null })} onCreate={create} creating={creating} /></motion.div>
          ) : null}
        </div>
        {staged || !created ? (
          <motion.div layout transition={{ layout: { duration: 0.5, ease: EASE } }} className={cn("flex w-full flex-col items-center px-4 sm:px-8", staged ? "flex-[1.25] pt-9" : "shrink-0 pb-4")} data-testid="intake-composer-slot">
            <div className={cn("w-full", staged ? "max-w-[760px]" : "max-w-[760px]")}>
              <IntakeComposer
                ref={composer}
                variant={staged ? "hero" : "chat"}
                globalDrop
                testId={staged ? "home-composer" : "intake-composer"}
                autoFocus={staged}
                placeholder={staged ? "Beskriv oppgaven, eller legg ved en forespørsel …" : session?.proposal ? "Spør om forespørselen, eller rett noe …" : "Beskriv forespørselen, eller legg til flere dokumenter …"}
                onSend={send}
                onFiles={intake.addFiles}
                busy={staged ? false : busy}
                attachments={staged ? rows : []}
                onRemoveAttachment={intake.remove}
                onRetryAttachment={intake.retry}
                hint={staged ? intakeProgress(rows, intake.phase, Boolean(session?.proposal)) : null}
              />
            </div>
            <AnimatePresence initial={false}>{staged ? <Starters onPick={pick} loading={loadingStarter} /> : null}</AnimatePresence>
          </motion.div>
        ) : null}
      </div>
      {viewer && intake.sid ? <DocViewer caseId={`inntak-${intake.sid}`} doc={viewerDoc} docId={viewer.docId} where={viewer.where} docs={docs} onPick={(docId) => setViewer({ docId, where: null })} onClose={() => setViewer(null)} mobile={!sm} /> : null}
    </div>
  );
}
